import { useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowLeft, Clock, CalendarRange, BedDouble, CarFront,
  Check, Info, ChevronDown, Sparkles,
} from "lucide-react";
import { TOURS } from "../data/content";
import { FadeUp, Img } from "../components/Reveal";
import { useEnquiry } from "../components/EnquiryContext";

const MetaItem = ({ icon: Icon, label, value }) => (
  <div className="p-6 border border-gold/10 bg-forest-900/40">
    <Icon size={18} className="text-gold mb-3" strokeWidth={1.5} />
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-fog block mb-1.5">
      {label}
    </span>
    <span className="text-sm text-cream/85 leading-snug block">{value}</span>
  </div>
);

const Faq = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gold/10 bg-forest-900/40">
      <button
        data-testid={`faq-${q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-serif text-lg text-cream">{q}</span>
        <ChevronDown
          size={16}
          className={`text-gold shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <p className="px-6 pb-6 text-sm text-sage leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
};

export default function TourDetail() {
  const { slug } = useParams();
  const tour = TOURS.find((t) => t.slug === slug);
  const { openEnquiry } = useEnquiry();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  if (!tour) return <Navigate to="/" replace />;

  return (
    <main data-testid="tour-detail-page" className="bg-forest-950">
      <section ref={ref} className="relative h-[75vh] min-h-[520px] overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <Img src={tour.img} alt={tour.name} className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/30 to-forest-950/60" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-16">
          <FadeUp>
            <Link
              to="/"
              data-testid="tour-back-link"
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-cream/60 hover:text-gold transition-colors mb-8"
            >
              <ArrowLeft size={13} /> All Tours
            </Link>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="flex items-center gap-4 mb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
                {tour.destination}
              </span>
              <span className="h-px w-10 bg-gold/40" />
              <span className="font-mono text-[11px] tracking-[0.2em] text-cream/70 flex items-center gap-2">
                <Clock size={12} /> {tour.duration}
              </span>
            </div>
          </FadeUp>
          <FadeUp delay={0.2}>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-cream tracking-tight max-w-3xl">
              {tour.name}
            </h1>
          </FadeUp>
          <FadeUp delay={0.3}>
            <p className="font-serif italic text-lg text-sage mt-3">{tour.tagline}</p>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-14">
          <div>
            <FadeUp>
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
                Tour Overview
              </span>
              <p className="mt-5 text-base text-cream/75 leading-relaxed" data-testid="tour-overview">
                {tour.overview}
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <h2 className="font-serif text-2xl sm:text-3xl text-cream mt-16 mb-8" data-testid="tour-highlights-head">
                Highlights
              </h2>
              <ul className="space-y-4">
                {tour.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-4 text-sm text-cream/75">
                    <span className="w-6 h-6 border border-gold/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={12} className="text-gold" />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>
            </FadeUp>

            <FadeUp delay={0.1}>
              <h2 className="font-serif text-2xl sm:text-3xl text-cream mt-16 mb-10" data-testid="tour-itinerary-head">
                Itinerary
              </h2>
              <div className="relative pl-8 border-l border-gold/20 space-y-10">
                {tour.itinerary.map((d, i) => (
                  <div key={d.day} className="relative" data-testid={`itinerary-${d.day.toLowerCase().replace(/\s+/g, "-")}`}>
                    <span className="absolute -left-[41px] top-0 w-5 h-5 rotate-45 bg-forest-950 border border-gold flex items-center justify-center">
                      <span className="w-1.5 h-1.5 bg-gold" />
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                      {d.day}
                    </span>
                    <h3 className="font-serif text-xl text-cream mt-1.5">{d.title}</h3>
                    <p className="text-sm text-cream/60 mt-2 leading-relaxed max-w-xl">{d.detail}</p>
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <h2 className="font-serif text-2xl sm:text-3xl text-cream mt-16 mb-8">Activities</h2>
              <div className="flex flex-wrap gap-3">
                {tour.activities.map((a) => (
                  <span
                    key={a}
                    className="flex items-center gap-2 border border-gold/20 text-cream/75 font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2.5"
                  >
                    <Sparkles size={11} className="text-gold" /> {a}
                  </span>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <h2 className="font-serif text-2xl sm:text-3xl text-cream mt-16 mb-8">Gallery</h2>
              <div className="grid grid-cols-2 gap-3">
                {tour.gallery.map((g, i) => (
                  <div key={i} className={`overflow-hidden group ${i === 0 ? "col-span-2 h-64 sm:h-80" : "h-44 sm:h-56"}`}>
                    <Img
                      src={g}
                      alt={`${tour.name} gallery ${i + 1}`}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                    />
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <h2 className="font-serif text-2xl sm:text-3xl text-cream mt-16 mb-8">FAQs</h2>
              <div className="space-y-3">
                {tour.faqs.map((f) => (
                  <Faq key={f.q} q={f.q} a={f.a} />
                ))}
              </div>
            </FadeUp>
          </div>

          <aside className="lg:sticky lg:top-28 self-start space-y-4" data-testid="tour-meta-aside">
            <div className="grid grid-cols-1 gap-4">
              <MetaItem icon={Clock} label="Duration" value={tour.duration} />
              <MetaItem icon={CalendarRange} label="Best Time to Visit" value={tour.bestTime} />
              <MetaItem icon={BedDouble} label="Accommodation" value={tour.accommodation} />
              <MetaItem icon={CarFront} label="Transportation" value={tour.transport} />
            </div>
            <div className="p-6 border border-gold/10 bg-forest-900/40">
              <div className="flex items-center gap-2 mb-4">
                <Info size={15} className="text-gold" />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                  Important Information
                </span>
              </div>
              <ul className="space-y-3">
                {tour.important.map((n) => (
                  <li key={n} className="text-xs text-cream/60 leading-relaxed flex gap-2.5">
                    <span className="w-1 h-1 rotate-45 bg-gold/70 mt-1.5 shrink-0" />
                    {n}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 glass-panel">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-fog block">
                Starting At
              </span>
              <span className="font-serif text-2xl text-gold mt-1 block">{tour.price}</span>
              <button
                data-testid="tour-book-btn"
                onClick={() => openEnquiry({ destination: tour.name })}
                className="w-full mt-5 bg-gold text-forest-950 font-mono text-xs uppercase tracking-[0.22em] py-4 hover:bg-cream transition-colors duration-300"
              >
                Book / Enquire
              </button>
            </div>
          </aside>
        </div>
      </section>

      <section className="relative py-24 border-t border-gold/10 overflow-hidden" data-testid="tour-cta-band">
        <div className="absolute inset-0">
          <Img src={tour.gallery[1] || tour.img} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-forest-950 via-forest-950/70 to-forest-950" />
        </div>
        <div className="relative max-w-3xl mx-auto px-5 text-center">
          <FadeUp>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
              Ready to Explore?
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream mt-5 leading-tight">
              The forest is waiting. <span className="italic text-gold">Let's plan it together.</span>
            </h2>
            <button
              data-testid="tour-cta-book-btn"
              onClick={() => openEnquiry({ destination: tour.name })}
              className="mt-9 bg-gold text-forest-950 font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-cream transition-colors duration-300"
            >
              Book This Journey
            </button>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
